/* eslint-disable react-hooks/exhaustive-deps */
import { jwtDecode } from "jwt-decode";
import { Suspense, createContext, useEffect, useMemo, useState } from "react";
import { Provider as StoreProvider } from "react-redux";

import AppNavigator from "./AppNavigator";
import LoggedInUserApi from "./api/LoggedInUserApi";
import { LoadingIndicator } from "./components/placeholders";
import { EToastvariant } from "./config/interfaces";
import store, { useAppDispatch, useAppSelector } from "./redux/Store";
import { fetchGlobalSettings } from "./redux/slice/AuthDataSlice";
import { loggedInUserSelector, setLoggedInUser } from "./redux/slice/LoggedInUserSlice";
import { showToastMsg } from "./redux/slice/ToastMsgSlice";
import { RulesService, StorageService, UtilService } from "./services";

interface IAppRulesContext {
	rulesSvc: RulesService;
}

export const AppRulesContext = createContext<IAppRulesContext>({
	rulesSvc: new RulesService([])
});

const AppContextWrapper = () => {
	const dispatch = useAppDispatch();
	const { loggedInUser } = useAppSelector(loggedInUserSelector);

	const [permissions, setPermissions] = useState<any[]>([]);
	const [loading, setLoading] = useState<boolean>(true);

	const decodeJWTWebToken = async () => {
		try {
			const _authToken = await StorageService.getApiToken();
			let decoded: any = [];
			if (_authToken) {
				decoded = jwtDecode(_authToken);
				setPermissions(decoded.permissions);
			}
		} catch (error) {
			dispatch(
				showToastMsg({
					isVisible: true,
					title: "Error",
					message: UtilService.getErrorMessage(error),
					variant: EToastvariant.Danger
				})
			);
		}
	};

	const _userPermissions = useMemo(() => {
		return permissions || [];
	}, [loggedInUser, permissions]);

	const init = async () => {
		try {
			setLoading(true);
			const token = await StorageService.getApiToken();
			if (token) {
				const apiSvc = LoggedInUserApi.getInstance();
				const profileResp = await apiSvc.getMyProfile();

				dispatch(setLoggedInUser(profileResp));
				await StorageService.setTermsAccepted(profileResp?.termsAcceptedAt);
				decodeJWTWebToken();
			}
		} catch (error: any) {
			console.log("Token validation failed ", error?.response?.data);
			if (!UtilService?.handleLogoutOnUnauthorized) {
				await StorageService.clearStorage();
			}
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		init();
	}, []);

	useEffect(() => {
		dispatch(fetchGlobalSettings());
	}, [loggedInUser]);

	useEffect(() => {
		decodeJWTWebToken();
	}, [loggedInUser]);

	return (
		<>
			{!loading ? (
				<AppRulesContext.Provider value={{ rulesSvc: new RulesService(_userPermissions || []) }}>
					<AppNavigator />
				</AppRulesContext.Provider>
			) : (
				<LoadingIndicator />
			)}
		</>
	);
};

const App = () => {
	return (
		<Suspense fallback={<LoadingIndicator />}>
			<StoreProvider store={store}>
				<AppContextWrapper />
			</StoreProvider>
		</Suspense>
	);
};

export default App;
